import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Snackbar,
  Chip
} from '@mui/material';
import { Add, Edit, Delete, CheckCircle, Cancel, Close } from '@mui/icons-material';
import commissionService from '../../services/commissionService';
import DialogHeader from '../Common/DialogHeader';
import RowActionsMenu from './shared/RowActionsMenu';
import ResponsiveTableContainer from './shared/ResponsiveTableContainer';
import { formatHuf } from '../../utils/currency';
import { useConfirm } from '../Common/ConfirmDialog';

const CommissionGroupsPage = () => {
  const [confirm, confirmDialog] = useConfirm();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null); // Szerkesztéshez
  const [formState, setFormState] = useState({
    name: '',
    percentage: '',
    fixedAmount: '',
    isActive: true,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const fetchCommissionGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await commissionService.getCommissionGroups();
      if (response.success) {
        setGroups(response.data);
      } else {
        setError(response.message || 'Jutalékcsoportok betöltése sikertelen.');
      }
    } catch (err) {
      console.error('CommissionGroupsPage - Error fetching commission groups:', err);
      setError('Hiba történt a jutalékcsoportok betöltése során.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCommissionGroups();
  }, [fetchCommissionGroups]);

  const handleOpenDialog = (group = null) => {
    setCurrentGroup(group);
    setFormState({
      name: group?.name || '',
      percentage: group?.percentage || '',
      fixedAmount: group?.fixedAmount || '',
      isActive: group?.isActive ?? true,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentGroup(null);
    setFormState({ name: '', percentage: '', fixedAmount: '', isActive: true });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSubmit = async () => {
    setActionLoading(true);
    setError(null);
    try {
      let response;
      const dataToSend = {
        ...formState,
        percentage: formState.percentage !== '' ? parseFloat(formState.percentage) : 0,
        fixedAmount: formState.fixedAmount !== '' ? parseFloat(formState.fixedAmount) : 0,
      };

      if (currentGroup) {
        response = await commissionService.updateCommissionGroup(currentGroup.id, dataToSend);
        showSnackbar('Jutalékcsoport sikeresen frissítve!', 'success');
      } else {
        response = await commissionService.createCommissionGroup(dataToSend);
        showSnackbar('Jutalékcsoport sikeresen létrehozva!', 'success');
      }

      if (response.success) {
        fetchCommissionGroups();
        handleCloseDialog();
      } else {
        setError(response.message || 'Hiba történt a művelet során.');
      }
    } catch (err) {
      console.error('Error submitting commission group:', err);
      setError(err.message || 'Hiba történt a művelet során.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({ title: 'Jutalékcsoport törlése', message: 'Biztosan törölni szeretné ezt a jutalékcsoportot?', confirmLabel: 'Törlés', color: 'error' }))) {
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      const response = await commissionService.deleteCommissionGroup(id);
      if (response.success) {
        showSnackbar('Jutalékcsoport sikeresen törölve!', 'success');
        fetchCommissionGroups();
      } else {
        setError(response.message || 'Hiba történt a törlés során.');
      }
    } catch (err) {
      console.error('Error deleting commission group:', err);
      setError(err.message || 'Hiba történt a törlés során.');
    } finally {
      setActionLoading(false);
    }
  };
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Új jutalékcsoport
        </Button>
      </Box>

      <ResponsiveTableContainer stickyLastColumn>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Név</TableCell>
              <TableCell align="right">Százalék (%)</TableCell>
              <TableCell align="right">Fix összeg (Ft)</TableCell>
              <TableCell>Aktív</TableCell>
              <TableCell align="center">Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nincsenek jutalékcsoportok.
                </TableCell>
              </TableRow>
            ) : (
              groups.map((group) => (
                <TableRow key={group.id}>
                  <TableCell>{group.name}</TableCell>
                  <TableCell align="right">{group.percentage}%</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatHuf(group.fixedAmount)}</TableCell>
                  <TableCell>
                    <Chip
                      label={group.isActive ? 'Aktív' : 'Inaktív'}
                      color={group.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <RowActionsMenu
                      primaryAction={{
                        icon: <Edit />,
                        color: 'primary',
                        iconOnly: true,
                        title: 'Szerkesztés',
                        onClick: () => handleOpenDialog(group),
                      }}
                      menuActions={[
                        { label: 'Törlés', icon: <Delete fontSize="small" />, color: 'error', onClick: () => handleDelete(group.id) },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ResponsiveTableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={currentGroup ? <Edit /> : <Add />}
            title={currentGroup ? 'Jutalékcsoport szerkesztése' : 'Új jutalékcsoport létrehozása'}
          />
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Név"
            type="text"
            fullWidth
            variant="outlined"
            value={formState.name}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="percentage"
            label="Százalék (%)"
            type="number"
            fullWidth
            variant="outlined"
            value={formState.percentage}
            onChange={handleChange}
            inputProps={{ step: "0.01" }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="fixedAmount"
            label="Fix összeg (Ft)"
            type="number"
            fullWidth
            variant="outlined"
            value={formState.fixedAmount}
            onChange={handleChange}
            inputProps={{ step: "0.01" }}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formState.isActive}
                onChange={handleChange}
                name="isActive"
                color="primary"
              />
            }
            label="Aktív"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={handleCloseDialog} disabled={actionLoading}>Mégse</Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={actionLoading || !formState.name}
            startIcon={actionLoading ? <CircularProgress size={20} /> : (currentGroup ? <Edit /> : <Add />)}
          >
            {currentGroup ? 'Mentés' : 'Létrehozás'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setSnackbarOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        }
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {confirmDialog}
    </Box>
  );
};

export default CommissionGroupsPage;